import { Stack } from "@mui/material";
import { chain, groupBy } from "lodash-es";
import { useMemo, useState } from "react";

import VehicleDamageList from "@/common/components/Entity/VehicleDamage/VehicleDamageList";
import VehicleDamageViewModal from "@/common/components/Entity/VehicleDamage/View/VehicleDamageViewModal";
import { Mapper } from "@/common/mapping/mapper";
import { VehicleArea, VehicleDamageDto, VehicleProjection } from "@/core/api/generated/v1";

import { VisualModelViewer } from "../VisualModelViewer";

export interface VehicleDamagesVisualModelViewerProps {
  entities: Nil<VehicleDamageDto[]>;
  withDamageList?: boolean;
  onUpdated: ((newValue: Nil<VehicleDamageDto>) => void) | undefined;
  onDeleted: ((newValue: Nil<VehicleDamageDto>) => void) | undefined;
}

/** Displays damages on different visual models. */
export function VisualModelViewerOfVehicleDamages({
  entities,
  withDamageList,
  onUpdated,
  onDeleted,
}: VehicleDamagesVisualModelViewerProps) {
  const [vehicleDamageToView, setVehicleDamageToView] = useState<VehicleDamageDto | null>(null);

  const damagesByVisualModelIdMap = useMemo(
    () =>
      groupBy(
        entities?.filter((x) => !!x.visualModel),
        (x) => x.visualModel?.id || "",
      ),
    [entities],
  );

  const pointsByVisualModelIdMap = useMemo(
    () =>
      chain(
        Object.entries(damagesByVisualModelIdMap).map(([visualModelId, damages]) => ({
          visualModelId,
          damages,
        })),
      )
        .keyBy((x) => x.visualModelId)
        .mapValues((x) =>
          x.damages
            .map((damage) => Mapper.visualModel.mapVehicleDamageDtoToVisualModelPoint(damage)!)
            .filter(Boolean),
        )
        .value(),

    [damagesByVisualModelIdMap],
  );

  return (
    <Stack spacing={2}>
      {Object.keys(damagesByVisualModelIdMap).map((visualModelId, i) => (
        <VisualModelViewer
          key={visualModelId || i}
          visualModelId={visualModelId}
          visualModel={undefined}
          initialMainImageSelector={{
            area: VehicleArea.Exterior,
            projection: VehicleProjection.Front,
          }}
          points={pointsByVisualModelIdMap[visualModelId || ""] || []}
          mainViewerProps={{
            onPointClicked: (params) => {
              if (params.point.data) {
                setVehicleDamageToView(params.point.data);
              }
            },
          }}
        />
      ))}

      {withDamageList && (
        <VehicleDamageList
          vehicleDamages={entities || []}
          displayProps={{
            vehicleColumn: true,
          }}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
        />
      )}

      {/* Vehicle damage view modal */}
      {vehicleDamageToView && (
        <VehicleDamageViewModal
          open={!!vehicleDamageToView}
          onClose={() => {
            setVehicleDamageToView(null);
          }}
          viewProps={{
            vehicleId: vehicleDamageToView.vehicle?.id,
            vehicleDamageId: vehicleDamageToView.id,
            vehicleDamage: vehicleDamageToView,
            onDelete: null,
          }}
        />
      )}
    </Stack>
  );
}
